import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraFormField, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueries } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { projectDataClient } from '../../../lib/connect/clients';
import { useEntityArtifacts } from '../documentHooks';
import { TextField } from '../PastPerformance/Fields';
import { useCustomerThemes } from '../themeHooks';
import { saveErrorMessage, useSaveWinTheme } from './winThemeHooks';
const emptyClaim = ()=>({
        claimText: '',
        proofs: []
    });
/** Create (no `theme`) or edit (appends a version) a Win Theme. */ export function WinThemeForm({ pursuitId, theme, onCancel, onSaved, onOpenCustomerThemes }) {
    const themes = useCustomerThemes(pursuitId);
    const accepted = (themes.data ?? []).filter((t)=>t.recordState === 'accepted');
    const details = useQueries({
        queries: accepted.map((t)=>({
                queryKey: [
                    'customer-theme',
                    pursuitId,
                    t.id
                ],
                queryFn: ()=>projectDataClient.getCustomerTheme({
                        captureProjectId: pursuitId,
                        themeId: t.id
                    })
            }))
    });
    const documents = useEntityArtifacts('capture_project', pursuitId);
    const save = useSaveWinTheme();
    const inFlight = useRef(false);
    const [themeId, setThemeId] = useState(theme?.customerThemeId ?? '');
    const [need, setNeed] = useState(theme?.needSummary ?? '');
    const [differentiator, setDifferentiator] = useState(theme?.differentiator ?? '');
    const [narrative, setNarrative] = useState(theme?.narrative ?? '');
    const [claims, setClaims] = useState(theme?.claims.length ? theme.claims.map((c)=>({
            claimText: c.claimText,
            proofs: c.proofLinks.map((p)=>({
                    kind: p.kind,
                    id: p.id,
                    label: p.accessible ? p.label || p.id : 'Restricted source'
                }))
        })) : [
        emptyClaim()
    ]);
    const [error, setError] = useState('');
    const titleOf = (id, index)=>{
        const d = details[index]?.data;
        const version = d?.versions.find((v)=>v.id === d.theme?.currentVersionId);
        return version?.title || `Customer Theme ${id.slice(0, 8)}`;
    };
    const evidence = details[accepted.findIndex((t)=>t.id === themeId)]?.data?.evidence.map((e)=>({
            kind: 'evidence',
            id: e.id,
            label: e.sourceExcerpt || e.sourceRef || 'Evidence'
        })) ?? [];
    const proofOptions = [
        ...evidence,
        ...(documents.data ?? []).map((a)=>({
                kind: 'document',
                id: a.artifactId,
                label: a.filename
            }))
    ];
    const editing = !!theme;
    const ready = !!themeId && !!need.trim() && claims.every((c)=>c.claimText.trim());
    function pickTheme(id) {
        setThemeId(id);
        const index = accepted.findIndex((t)=>t.id === id);
        if (!need.trim() && index >= 0) setNeed(titleOf(id, index));
        // Evidence belongs to the chosen theme; drop evidence proofs from another one.
        setClaims((all)=>all.map((c)=>({
                    ...c,
                    proofs: c.proofs.filter((p)=>p.kind !== 'evidence')
                })));
    }
    const patch = (index, next)=>setClaims((all)=>all.map((c, i)=>i === index ? {
                    ...c,
                    ...next
                } : c));
    async function submit() {
        if (inFlight.current || !ready) return;
        inFlight.current = true;
        setError('');
        try {
            await save.mutateAsync({
                pursuitId,
                winThemeId: theme?.id ?? '',
                customerThemeId: themeId,
                needSummary: need.trim(),
                differentiator: differentiator.trim(),
                narrative: narrative.trim(),
                claims: claims.map((c)=>({
                        claimText: c.claimText.trim(),
                        proofLinks: c.proofs.map((p)=>({
                                kind: p.kind,
                                id: p.id
                            }))
                    }))
            });
            onSaved();
        } catch (cause) {
            setError(saveErrorMessage(cause));
        } finally{
            inFlight.current = false;
        }
    }
    if (themes.isPending) return /*#__PURE__*/ _jsx("output", {
        className: "text-sm",
        children: "Loading Customer Themes…"
    });
    if (themes.isError) return /*#__PURE__*/ _jsxs("div", {
        role: "alert",
        className: "text-sm",
        children: [
            "Customer Themes could not be loaded.",
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                onClick: ()=>themes.refetch(),
                children: "Retry"
            })
        ]
    });
    if (!accepted.length && !editing) return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.winThemes.emptyState,
        className: "space-y-2 text-sm",
        children: [
            /*#__PURE__*/ _jsx("p", {
                children: "No Customer Themes have been accepted yet. Only reviewed Customer Themes can be used. Review and accept a theme in Customer Themes first."
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                onClick: onOpenCustomerThemes,
                children: "Open Customer Themes"
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "ghost",
                onClick: onCancel,
                children: "Cancel"
            })
        ]
    });
    return /*#__PURE__*/ _jsxs("form", {
        "data-testid": Testids.winThemes.form,
        className: "space-y-3",
        onSubmit: (event)=>{
            event.preventDefault();
            void submit();
        },
        children: [
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Customer Theme (customer need)",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "data-testid": Testids.winThemes.needSelect,
                    value: themeId,
                    disabled: editing,
                    onChange: (event)=>pickTheme(event.target.value),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Choose a reviewed Customer Theme"
                        }),
                        accepted.map((t, index)=>/*#__PURE__*/ _jsx("option", {
                                value: t.id,
                                children: titleOf(t.id, index)
                            }, t.id)),
                        editing && !accepted.some((t)=>t.id === themeId) && /*#__PURE__*/ _jsx("option", {
                            value: themeId,
                            children: theme?.customerThemeTitle
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Only reviewed Customer Themes can be used."
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Customer need",
                testId: Testids.winThemes.needInput,
                value: need,
                onChange: setNeed
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Differentiator",
                testId: Testids.winThemes.differentiatorInput,
                value: differentiator,
                onChange: setDifferentiator,
                multiline: true
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Narrative",
                testId: Testids.winThemes.narrativeInput,
                value: narrative,
                onChange: setNarrative,
                multiline: true
            }),
            /*#__PURE__*/ _jsxs("fieldset", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("legend", {
                        className: "font-medium",
                        children: "Claims and proof"
                    }),
                    claims.map((claim, index)=>/*#__PURE__*/ _jsxs("div", {
                            "data-testid": Testids.winThemes.claim,
                            className: "space-y-2 rounded-md border p-2",
                            children: [
                                /*#__PURE__*/ _jsx(TextField, {
                                    label: `Claim ${index + 1}`,
                                    testId: Testids.winThemes.claimInput,
                                    value: claim.claimText,
                                    onChange: (value)=>patch(index, {
                                            claimText: value
                                        }),
                                    multiline: true
                                }),
                                /*#__PURE__*/ _jsx("ul", {
                                    className: "text-sm",
                                    children: claim.proofs.map((p)=>/*#__PURE__*/ _jsxs("li", {
                                            children: [
                                                "Proof (",
                                                p.kind,
                                                "): ",
                                                p.label,
                                                ' ',
                                                /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "sm",
                                                    variant: "ghost",
                                                    onClick: ()=>patch(index, {
                                                            proofs: claim.proofs.filter((x)=>x !== p)
                                                        }),
                                                    children: "Remove proof"
                                                })
                                            ]
                                        }, `${p.kind}:${p.id}`))
                                }),
                                !claim.proofs.length && /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm",
                                    children: "No proof attached — this claim will show as Unsupported."
                                }),
                                /*#__PURE__*/ _jsxs(SpecraSelect, {
                                    "aria-label": `Add proof to claim ${index + 1}`,
                                    "data-testid": Testids.winThemes.proofSelect,
                                    value: "",
                                    onChange: (event)=>{
                                        const [kind, ...rest] = event.target.value.split(':');
                                        const id = rest.join(':');
                                        const option = proofOptions.find((o)=>o.kind === kind && o.id === id);
                                        if (option && !claim.proofs.some((p)=>p.kind === kind && p.id === id)) patch(index, {
                                            proofs: [
                                                ...claim.proofs,
                                                option
                                            ]
                                        });
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "",
                                            children: "Add proof from this Pursuit's evidence or documents"
                                        }),
                                        proofOptions.map((o)=>/*#__PURE__*/ _jsxs("option", {
                                                value: `${o.kind}:${o.id}`,
                                                children: [
                                                    o.kind === 'evidence' ? 'Evidence' : 'Document',
                                                    ": ",
                                                    o.label
                                                ]
                                            }, `${o.kind}:${o.id}`))
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "ghost",
                                    "data-testid": Testids.winThemes.removeClaim,
                                    onClick: ()=>setClaims((all)=>all.filter((_, i)=>i !== index)),
                                    children: "Remove claim"
                                })
                            ]
                        }, index)),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.winThemes.addClaim,
                        onClick: ()=>setClaims((all)=>[
                                    ...all,
                                    emptyClaim()
                                ]),
                        children: "Add claim"
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                "data-testid": Testids.winThemes.formError,
                children: error
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: editing ? 'Saving creates a new version; earlier versions stay in history.' : 'Saving creates version 1 of this Win Theme.'
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                type: "submit",
                "data-testid": Testids.winThemes.saveButton,
                disabled: save.isPending || !ready,
                children: save.isPending ? 'Saving…' : editing ? 'Save as new version' : 'Save Win Theme'
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "ghost",
                "data-testid": Testids.winThemes.cancelButton,
                onClick: onCancel,
                children: "Cancel"
            })
        ]
    });
}
